import { List, Header, Text, ModalWrapper, TextInput, Button, MCIcon, Image, ActivityIndicator, NotificationButton } from "@src/components"
import { ar } from "@src/i18n/ar"
import { formatDateForDisplay, priceFromDBFormat, sharedStyles, trpc, ENV, RouterOutputs, statusColors, isOrderEditable } from "@src/utils"
import { useLocalSearchParams, router, Link } from "expo-router"
import { Platform, Pressable, View } from "react-native"
import { Alert } from "@src/components/ui/confirm"
import { SafeAreaView } from "react-native-safe-area-context"
import { ORDER_TRANSITIONS } from "@white-labels/utils"
import { useState, ComponentProps } from "react"
import { OrderStatus } from "@white-labels/input"
import { OrderChangeChange } from "../../../../../../packages/db2/src/custom"

type IconName = ComponentProps<typeof MCIcon>["name"]
type Order = NonNullable<RouterOutputs["orders"]["get"]>

const changeIcons: Record<string, IconName> = {
  status: "swap-horizontal",
  items: "cart",
  address: "map-marker",
  discount: "brightness-percent",
  created: "plus-circle",
}

/** Arabic labels for the order `source` field (used in the activity log "created" entry). */
const sourceLabels: Record<string, string> = {
  app: ar.app,
  phone: ar.phone,
  whatsapp: ar.whatsapp,
  pos: ar.pos,
  manual: ar.manual,
}

/** Arabic labels for each order status (used in the activity log "status" entry). */
const statusLabels: Record<string, string> = {
  pending: ar.pending,
  confirmed: ar.confirmed,
  preparing: ar.preparing,
  out_for_delivery: ar.out_for_delivery,
  delivered: ar.delivered,
  cancelled: ar.cancelled,
  rejected: ar.rejected,
  delivery_failed: ar.delivery_failed,
}

/** Arabic label for each status transition button. */
const statusActionMap: Record<OrderStatus, string> = {
  confirmed: ar.confirmOrder,
  rejected: ar.rejectOrder,
  cancelled: ar.cancelOrder,
  preparing: ar.startPreparing,
  out_for_delivery: ar.markOutForDelivery,
  delivered: ar.markDelivered,
  delivery_failed: ar.markDeliveryFailed,
  pending: "",
}

function describeChange(change: OrderChangeChange): { icon: IconName; description: string } {
  const icon = changeIcons[change.field] || ("information" as IconName)

  let description: string
  if (change.field === "status") {
    const from = change.from
    const to = change.to
    description = `${ar.statusChanged}: ${statusLabels[from] ?? from} → ${statusLabels[to] ?? to}`
  } else if (change.field === "items") {
    description = ar.itemsChanged
  } else if (change.field === "address") {
    description = ar.addressChanged
  } else if (change.field === "discount") {
    const from = change.from
    const to = change.to
    description = `${ar.discountChanged}: ${priceFromDBFormat(from ?? 0)} → ${priceFromDBFormat(to ?? 0)} ${ar.egp}`
  } else if (change.field === "created") {
    const source = change.source
    description = `${ar.orderCreated} (${sourceLabels[source] ?? source})`
  } else {
    description = JSON.stringify(change)
  }

  return { icon, description }
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const color = statusColors[status]
  return (
    <View className="px-3 py-1 rounded" style={{ backgroundColor: `${color}20` }}>
      <Text className="font-semibold" style={{ color }}>
        {ar[status]}
      </Text>
    </View>
  )
}

function OrderInfoCard({ order }: { order: Order }) {
  return (
    <View className="bg-white p-4 rounded-lg border border-gray-200 mb-2">
      <View className="flex-row justify-between items-start mb-3">
        <View>
          <Text className="text-gray-600 text-sm mb-1">{ar.orderNumber}</Text>
          <Text className="text-2xl font-bold text-gray-800">#{order.orderNumber}</Text>
        </View>
        <StatusBadge status={order.status} />
      </View>

      <View className="border-t border-gray-100 pt-3 gap-2">
        <Link asChild href={`/dashboard/customer/${order.customer?.id}`}>
          <Pressable className="flex-row justify-between">
            <Text className="text-gray-600">{ar.customers}</Text>
            <Text className="font-semibold text-gray-800">{order.customer?.name}</Text>
          </Pressable>
        </Link>
        <View className="flex-row justify-between">
          <Text className="text-gray-600">{ar.orderSource}</Text>
          <Text className="font-semibold text-gray-800">{ar[order.source]}</Text>
        </View>
        <View className="flex-row justify-between">
          <Text className="text-gray-600">{ar.created}</Text>
          <Text className="text-gray-800">{formatDateForDisplay(order.createdAt)}</Text>
        </View>
        {order.updatedAt && (
          <View className="flex-row justify-between">
            <Text className="text-gray-600">{ar.lastUpdated}</Text>
            <Text className="text-gray-800">{formatDateForDisplay(order.updatedAt)}</Text>
          </View>
        )}
      </View>
    </View>
  )
}

function FinancialSummaryCard({ order }: { order: Order }) {
  return (
    <View className="bg-white p-4 rounded-lg border border-gray-200 mb-2">
      <Text className="font-bold text-lg text-gray-800 mb-3">{ar.orderSummary}</Text>
      <View className="gap-2">
        <View className="flex-row justify-between">
          <Text className="text-gray-600">{ar.subtotal}</Text>
          <Text className="text-gray-800">
            {priceFromDBFormat(order.subtotal)} {ar.egp}
          </Text>
        </View>
        <View className="flex-row justify-between">
          <Text className="text-gray-600">{ar.deliveryFee}</Text>
          <Text className="text-gray-800">
            {priceFromDBFormat(order.deliveryFee)} {ar.egp}
          </Text>
        </View>
        {order.discount > 0 && (
          <View className="flex-row justify-between">
            <Text className="text-gray-600">{ar.discount}</Text>
            <Text className="text-red-600">
              -{priceFromDBFormat(order.discount)} {ar.egp}
            </Text>
          </View>
        )}
        <View className="flex-row justify-between pt-2 border-t border-gray-200">
          <Text className="font-bold text-gray-800">{ar.total}</Text>
          <Text className="font-bold text-lg text-gray-800">
            {priceFromDBFormat(order.total)} {ar.egp}
          </Text>
        </View>
      </View>
    </View>
  )
}

function DeliveryInfoCard({ order }: { order: Order }) {
  return (
    <View className="bg-white p-4 rounded-lg border border-gray-200 mb-2">
      <Text className="font-bold text-lg text-gray-800 mb-3">{ar.deliveryInfo}</Text>
      <View className="gap-3">
        {order.addressJson ? (
          <View className="bg-gray-50 p-3 rounded-lg border border-gray-100 gap-2">
            <View className="flex-row items-center justify-between mb-1">
              <Text className="font-bold text-gray-800 text-base">{order.addressJson.label}</Text>
              {order.deliveryAreaJson?.name && (
                <View className="bg-blue-100 px-2 py-1 rounded">
                  <Text className="text-blue-800 text-xs font-semibold">{order.deliveryAreaJson.name}</Text>
                </View>
              )}
            </View>

            <View className="flex-row flex-wrap gap-x-6 gap-y-2">
              {order.addressJson.building && (
                <View>
                  <Text className="text-gray-500 text-xs mb-0.5">{ar.building}</Text>
                  <Text className="text-gray-800 font-medium">{order.addressJson.building}</Text>
                </View>
              )}
              {order.addressJson.floor && (
                <View>
                  <Text className="text-gray-500 text-xs mb-0.5">{ar.floor}</Text>
                  <Text className="text-gray-800 font-medium">{order.addressJson.floor}</Text>
                </View>
              )}
              {order.addressJson.flat && (
                <View>
                  <Text className="text-gray-500 text-xs mb-0.5">{ar.flat}</Text>
                  <Text className="text-gray-800 font-medium">{order.addressJson.flat}</Text>
                </View>
              )}
            </View>

            {order.addressJson.details && (
              <View className="mt-1 pt-2 border-t border-gray-200">
                <Text className="text-gray-500 text-xs mb-0.5">{ar.addressDetails}</Text>
                <Text className="text-gray-800">{order.addressJson.details}</Text>
              </View>
            )}
          </View>
        ) : (
          <Text className="text-gray-500 italic">N/A</Text>
        )}

        {order.deliveryInstructions ? (
          <View className="bg-yellow-50 p-3 rounded-lg border border-yellow-100">
            <View className="flex-row items-center gap-2 mb-1">
              <MCIcon name="information" size={16} color="#eab308" />
              <Text className="text-yellow-800 font-semibold">{ar.deliveryInstructions}</Text>
            </View>
            <Text className="text-yellow-900">{order.deliveryInstructions}</Text>
          </View>
        ) : null}
      </View>
    </View>
  )
}

// function OrderItemRow({ item, product }: { item: OrderItem; product?: Product })

function OrderStatusActions({ orderId, currentStatus }: { orderId: number; currentStatus: OrderStatus }) {
  const updateStatus = trpc.orders.updateStatus.useMutation()
  const utils = trpc.useUtils()
  const [showRejectModal, setShowRejectModal] = useState(false)

  const doStatusChange = async (newStatus: OrderStatus, reason?: string) => {
    try {
      await updateStatus.mutateAsync({ id: orderId, status: newStatus, rejectionReason: reason })
      await utils.orders.get.invalidate({ id: orderId })
      await utils.orders.list.invalidate()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update status"
      Alert.alert(ar.error, errorMessage)
    }
  }

  const handleStatusChange = (newStatus: OrderStatus) => {
    if (newStatus === "rejected") {
      setShowRejectModal(true)
      return
    }

    if (newStatus === "cancelled" && currentStatus === "out_for_delivery") {
      Alert.alert(ar.cancelOrder, ar.cancelInFlightWarning, [
        { text: ar.keepOrder, style: "cancel" },
        { text: ar.cancelOrder, style: "destructive", onPress: () => doStatusChange("cancelled") },
      ])
      return
    }

    void doStatusChange(newStatus)
  }

  const transitions = ORDER_TRANSITIONS[currentStatus] ?? []

  return (
    <>
      <View className="bg-white p-4 rounded-lg border border-gray-200">
        <Text className="font-bold text-lg text-gray-800 mb-3">{ar.orderStatus}</Text>
        <View className="flex-row flex-wrap gap-2">
          {transitions.map((status) => (
            <Pressable
              key={status}
              onPress={() => handleStatusChange(status)}
              className="px-4 py-2 rounded-sm"
              style={{ backgroundColor: statusColors[status] }}
              disabled={updateStatus.isPending}
            >
              <Text className="text-white font-semibold">{statusActionMap[status]}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      {showRejectModal && (
        <RejectReasonModal onClose={() => setShowRejectModal(false)} onSubmit={(reason) => doStatusChange("rejected", reason)} disabled={updateStatus.isPending} />
      )}
    </>
  )
}

function ActivityLog({ changes }: { changes: Order["changes"] }) {
  return (
    <>
      <View className="bg-white p-4 rounded-lg border border-gray-200">
        <Text className="font-bold text-lg text-gray-800">{ar.activityLog}</Text>
      </View>
      <View>
        {changes.map((item) => {
          const { icon, description } = describeChange(item.change)
          return (
            <View className="bg-white p-3 mb-2 rounded-lg border border-gray-100 flex-row gap-3" key={item.id}>
              <View className="w-8 h-8 rounded-full bg-blue-100 items-center justify-center">
                <MCIcon name={icon} size={16} color="#3b82f6" />
              </View>
              <View className="flex-1">
                <Text className="text-gray-800 mb-1">{description}</Text>
                <Text className="text-xs text-gray-500">{formatDateForDisplay(item.createdAt)}</Text>
              </View>
            </View>
          )
        })}
      </View>
    </>
  )
}

function RejectReasonModal({ onClose, onSubmit, disabled }: { onClose: () => void; onSubmit: (reason: string) => void; disabled: boolean }) {
  const [reason, setReason] = useState("")

  const handleSubmit = () => {
    if (!reason.trim()) {
      Alert.alert(ar.error, ar.enterRejectionReason)
      return
    }
    onSubmit(reason)
    onClose()
  }

  return (
    <ModalWrapper onClose={onClose}>
      <Text className="text-lg font-bold mb-4 text-center">{ar.rejectOrder}</Text>
      <Text className="mb-2 text-gray-700">{ar.rejectionReason}</Text>
      <TextInput placeholder={ar.enterRejectionReason} value={reason} onChangeText={setReason} multiline numberOfLines={3} className="mb-3" />

      <View className="flex-row gap-3">
        <Button onPress={onClose} className="flex-1 bg-gray-600">
          {ar.cancel}
        </Button>
        <Button onPress={handleSubmit} disabled={disabled} className="flex-1 bg-red-600">
          {ar.rejectOrder}
        </Button>
      </View>
    </ModalWrapper>
  )
}

export default function OrderDetails() {
  const params = useLocalSearchParams<{ id: string }>()
  const id = Number.parseInt(params?.id, 10)

  const order = trpc.orders.get.useQuery({ id })
  const products = trpc.products.getMultiple.useQuery({ ids: order.data?.items.map((item) => item.productId) || [] })

  if (order.isLoading || products.isLoading) {
    return (
      <>
        <Header title={ar.orderDetails} />
        <ActivityIndicator className="mt-4" />
      </>
    )
  }

  if (!order.data) {
    return (
      <>
        <Header title={ar.orderDetails} />
        <View className="flex-1 justify-center items-center">
          <Text>{ar.errorLoading}</Text>
        </View>
      </>
    )
  }

  const orderData = order.data
  const canEdit = isOrderEditable(orderData.status)

  const webRefresh = Platform.OS === "web" && (
    <Pressable hitSlop={12} onPress={() => order.refetch()} disabled={order.isPending} className="h-full justify-center items-center px-2">
      <MCIcon name="refresh" size={22} color="#4b5563" />
    </Pressable>
  )

  const editBtn = canEdit ? (
    <Pressable
      hitSlop={12}
      onPress={() => {
        const warning = orderData.status === "preparing" ? ar.editPreparingWarning : orderData.status === "out_for_delivery" ? ar.editOutForDeliveryWarning : ar.editOrderWarning
        Alert.alert(ar.editOrder, warning, [
          { text: ar.cancel, style: "cancel" },
          { text: ar.editOrder, onPress: () => router.push(`/dashboard/orders/${id}/edit`) },
        ])
      }}
      className="h-full justify-center items-center px-2"
    >
      <MCIcon name="pencil" size={22} color="#3b82f6" />
    </Pressable>
  ) : undefined

  const notifyBtn = orderData.customer?.id ? <NotificationButton customerId={orderData.customer.id} /> : undefined

  return (
    <>
      <Header
        title={ar.orderDetails}
        right={
          <View className="h-full flex-row">
            {webRefresh}
            {notifyBtn}
            {editBtn}
          </View>
        }
      />
      <List
        ListHeaderComponent={
          <>
            <OrderInfoCard order={orderData} />
            <FinancialSummaryCard order={orderData} />
            <DeliveryInfoCard order={orderData} />
          </>
        }
        className="bg-gray-100"
        contentContainerStyle={sharedStyles.contentContainer}
        data={orderData.items ?? []}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => {
          const product = products.data?.products.find((p) => p.id === item.productId)
          const productImage = product?.images?.at(-1)
          return (
            <Link href={`/dashboard/products/${item.productId}`} asChild>
              <Pressable className="flex-row items-center mb-1 border-b border-gray-100 active:opacity-70 bg-white">
                <Image
                  source={productImage ? { uri: `${ENV.R2_URL}/${productImage.key}` } : undefined}
                  className={`w-16 h-16 m-1 ${productImage ? "" : "bg-gray-200"}`}
                  contentFit="contain"
                  transition={250}
                  loading="lazy"
                  cachePolicy="memory"
                />

                <View className="flex-1">
                  <Text className="text-gray-800 font-semibold" numberOfLines={1}>
                    {item.name}
                  </Text>
                  {item.upc && <Text className="text-gray-500 text-xs">{item.upc}</Text>}
                  <View className="flex-row items-center gap-2 mt-1">
                    <Text className="text-gray-600 text-sm">
                      {priceFromDBFormat(item.unitPrice)} {ar.egp}
                    </Text>
                    <Text className="text-gray-400 text-xs">×</Text>
                    <Text className="text-gray-600 text-sm font-bold">{item.quantity}</Text>
                    <Text className="text-gray-400 text-xs">=</Text>
                    <Text className="text-blue-600 text-sm font-bold">
                      {priceFromDBFormat(item.lineTotal)} {ar.egp}
                    </Text>
                  </View>
                </View>

                <View className="w-8 h-8 bg-gray-100 rounded items-center justify-center mx-2">
                  <Text className="font-semibold text-gray-700">{item.quantity}</Text>
                </View>
              </Pressable>
            </Link>
          )
        }}
        ListEmptyComponent={() => null}
        ListFooterComponent={
          <>
            <OrderStatusActions orderId={id} currentStatus={orderData.status} />
            <ActivityLog changes={orderData.changes} />
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />
    </>
  )
}
